import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import ReactDom from "react-dom";
import mapboxgl from "mapbox-gl";
import { useMapElement } from ".";

import styles from "./MapElement.module.scss";
import Markdown from "markdown-to-jsx";
import { truncateWords } from "../../../utils/utils";
import { CreativeMarkdown } from "../../common/CreativeMarkdown";

interface MarkerProps {
  position: mapboxgl.LngLatLike;
  open?: boolean;
  title: string;
  description: string;
  to: string;
  number: number;
  onActive: (num: number, force?: boolean) => void;
}

const Marker: React.FC<MarkerProps> = ({
  position,
  open,
  title,
  description,
  to,
  number,
  onActive,
}) => {
  const { map } = useMapElement();
  const markerRef = useRef<mapboxgl.Marker>();
  const popupRef = useRef<mapboxgl.Popup>();
  const [internalPopupOpen, setInternalPopupOpen] = useState(false);

  useEffect(() => {
    if (!map) {
      return;
    }

    const div = document.createElement("div");
    div.classList.add(styles.marker);
    ReactDom.render(
      <>
        <span>{number}</span>
      </>,
      div
    );

    markerRef.current = new mapboxgl.Marker(div).setLngLat(position).addTo(map);

    const onClickCb = () => {
      onActive(number, true);
      setInternalPopupOpen(state => !state);
    };

    div.addEventListener("click", onClickCb);

    return () => {
      div.removeEventListener("click", onClickCb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    setInternalPopupOpen(!!open);
  }, [open]);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (internalPopupOpen) {
      const popupDiv = document.createElement("div");
      popupDiv.classList.add(styles.popup);
      ReactDom.render(
        <>
          <span className={styles.popupTitle}>{title}</span>
          <CreativeMarkdown
            className={styles.popupDescription}
            options={{ forceBlock: true }}
          >
            {truncateWords(description, 40)}
          </CreativeMarkdown>
          <Link className={styles.popupLink} to={to}>
            Find out more
          </Link>
        </>,
        popupDiv
      );

      const popup = new mapboxgl.Popup({
        closeButton: false,
      })
        .setDOMContent(popupDiv)
        .setLngLat(position)
        .addTo(map);

      popup.addClassName(styles.popupWrapper);
      popup.setMaxWidth("300px");

      popup.on("close", () => {
        onActive(number, false);
        setInternalPopupOpen(false);
      });

      popupRef.current = popup;
    } else {
      popupRef.current?.remove();
    }

    markerRef.current
      ?.getElement()
      .classList.toggle(styles.open, internalPopupOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalPopupOpen, map]);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
    };
  }, []);

  return null;
};

export { Marker };
